import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Search, ArrowLeftRight, ChevronLeft, ChevronRight,
  Activity, ChevronDown, X
} from 'lucide-react';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';

// ── Constantes ────────────────────────────────────────────────────────────────

const TYPES = [
  { value: null,            label: 'Tous',           color: 'var(--primary-solid)' },
  { value: 'entree',        label: 'Entrées',         color: '#15803D' },
  { value: 'sortie',        label: 'Sorties',         color: '#DC2626' },
  { value: 'retour_vente',  label: 'Retours vente',   color: '#7C3AED' },
  { value: 'ajustement',    label: 'Ajustements',     color: '#D97706' },
  { value: 'deplacement',   label: 'Déplacements',    color: '#0369A1' },
  { value: 'inventaire',    label: 'Inventaires',     color: '#0284C7' },
];

const TYPE_META = {
  entree:       { label: 'Entrée',        color: '#15803D', bg: '#DCFCE7' },
  sortie:       { label: 'Sortie',        color: '#DC2626', bg: '#FEE2E2' },
  retour_vente: { label: 'Retour vente',  color: '#7C3AED', bg: '#EDE9FE' },
  ajustement:   { label: 'Ajustement',    color: '#D97706', bg: '#FEF3C7' },
  deplacement:  { label: 'Déplacement',   color: '#0369A1', bg: '#DBEAFE' },
  inventaire:   { label: 'Inventaire',    color: '#0284C7', bg: '#E0F2FE' },
};

const PAGE_SIZE = 50;

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function Delta({ avant, apres }) {
  const a = avant != null ? parseFloat(avant) : null;
  const b = apres != null ? parseFloat(apres) : null;
  if (a === null || b === null || isNaN(a) || isNaN(b)) return <span className="text-xs" style={{ color: 'var(--text-muted)' }}>—</span>;
  const diff = b - a;
  if (diff === 0) return <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>±0</span>;
  return (
    <span className="text-xs font-semibold" style={{ color: diff > 0 ? '#15803D' : '#DC2626' }}>
      {diff > 0 ? '+' : ''}{diff % 1 === 0 ? diff : diff.toFixed(2)}
    </span>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function MouvementsStock() {
  const { activeAgences } = useSociete();

  const [items, setItems]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [stocks, setStocks]         = useState([]);
  const [motifs, setMotifs]         = useState([]);

  const [typeFilter, setTypeFilter] = useState(null);
  const [search, setSearch]         = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedDepots, setSelectedDepots] = useState([]);
  const [motifFilters, setMotifFilters] = useState([]); // multi-select
  const [motifDropOpen, setMotifDropOpen] = useState(false);
  const [page, setPage]             = useState(0);

  const searchTimer = useRef(null);
  const motifRef    = useRef(null);

  // Fermer le dropdown motif si clic extérieur
  useEffect(() => {
    const h = (e) => { if (motifRef.current && !motifRef.current.contains(e.target)) setMotifDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Charger dépôts + motifs au montage
  useEffect(() => {
    api.getStocks().then(r => setStocks(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.getMotisAjustement().then(r => setMotifs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // Agence active unique
  const agenceId = activeAgences.length === 1 ? activeAgences[0].agence_id : null;

  const load = useCallback(async (pg = 0) => {
    setLoading(true);
    try {
      const resp = await api.getMouvementsGlobal({
        type_mvt:    typeFilter || undefined,
        stock_ids:   selectedDepots.length ? selectedDepots.join(',') : undefined,
        agence_id:   agenceId || undefined,
        search:      search || undefined,
        commentaire: motifFilters.length ? motifFilters.join(',') : undefined,
        limit:       PAGE_SIZE,
        offset:      pg * PAGE_SIZE,
      });
      setItems(resp.data?.items || []);
      setTotal(resp.data?.total || 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, selectedDepots, agenceId, search, motifFilters]);

  useEffect(() => { setPage(0); load(0); }, [load]);

  // Debounce recherche
  const handleSearchChange = (v) => {
    setSearchInput(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(v), 400);
  };

  const toggleMotif = (libelle) =>
    setMotifFilters(prev =>
      prev.includes(libelle) ? prev.filter(x => x !== libelle) : [...prev, libelle]
    );

  const toggleDepot = (id) =>
    setSelectedDepots(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const changePage = (p) => { setPage(p); load(p); };

  // Dépôts filtrés par agence active
  const stocksFiltered = agenceId
    ? stocks.filter(s => s.agence_id === agenceId)
    : stocks;

  return (
    <div className="space-y-5" data-testid="mouvements-stock-page">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>
            Mouvements de stock
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            Historique complet des entrées, sorties, ajustements et déplacements
            {agenceId && activeAgences[0] && (
              <span className="ml-1 font-medium" style={{ color: 'var(--primary-solid)' }}>
                · {activeAgences[0].nom}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
          style={{ background: 'var(--primary-50)', border: '1px solid var(--primary-100)' }}>
          <Activity size={14} style={{ color: 'var(--primary-solid)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--primary-solid)' }}>
            {total.toLocaleString('fr-FR')} mouvement{total > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* ── Filtres type (pills) ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        {TYPES.map(t => (
          <button key={String(t.value)} type="button"
            data-testid={`filter-type-${t.value ?? 'tous'}`}
            onClick={() => setTypeFilter(t.value)}
            className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
            style={{
              background:   typeFilter === t.value ? t.color : 'white',
              color:        typeFilter === t.value ? 'white' : 'var(--text-muted)',
              borderColor:  typeFilter === t.value ? t.color : 'var(--border)',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Recherche + Motif + Dépôts ──────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3 items-center">

        {/* Barre de recherche — largeur doublée */}
        <div className="relative w-[32rem]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: 'var(--text-placeholder)' }} />
          <input
            type="text" value={searchInput} onChange={e => handleSearchChange(e.target.value)}
            placeholder="Référence, désignation, marque, motif…"
            data-testid="mouvements-search"
            className="w-full pl-9 pr-4 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)', background: 'white' }} />
        </div>

        {/* Filtre motif — multi-select checkboxes */}
        <div className="relative w-[32rem]" ref={motifRef}>
          <button type="button"
            data-testid="motif-filter-btn"
            onClick={() => setMotifDropOpen(o => !o)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm"
            style={{
              borderColor: motifFilters.length ? 'var(--primary-solid)' : 'var(--border)',
              background:  motifFilters.length ? 'var(--primary-50)' : 'white',
              color:       motifFilters.length ? 'var(--primary-solid)' : 'var(--text-muted)',
            }}>
            <span className="truncate">
              {motifFilters.length === 0
                ? 'Filtrer par motif…'
                : motifFilters.length === 1
                  ? motifFilters[0]
                  : `${motifFilters.length} motifs sélectionnés`}
            </span>
            <span className="flex items-center gap-1.5 flex-shrink-0">
              {motifFilters.length > 0 && (
                <span
                  onClick={e => { e.stopPropagation(); setMotifFilters([]); }}
                  className="cursor-pointer rounded-full p-0.5 hover:bg-primary-100 transition-colors">
                  <X size={12} />
                </span>
              )}
              <ChevronDown size={13} style={{ transform: motifDropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
            </span>
          </button>

          {motifDropOpen && (
            <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow-lg overflow-hidden"
              style={{ borderColor: 'var(--border)' }}>
              {/* Pas de max-height → liste complète */}
              {motifs.length === 0 ? (
                <p className="px-3 py-3 text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                  Aucun motif défini — configurez-les dans Paramètres
                </p>
              ) : (
                <div className="overflow-y-auto py-1">
                  {motifs.map(m => (
                    <label key={m.motif_id}
                      className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{ color: motifFilters.includes(m.libelle) ? 'var(--primary-solid)' : 'var(--text)' }}>
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={motifFilters.includes(m.libelle)}
                        onChange={() => toggleMotif(m.libelle)}
                      />
                      <span className="text-sm">{m.libelle}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Checkboxes dépôts — alignés verticalement */}
        {stocksFiltered.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-medium flex-shrink-0" style={{ color: 'var(--text-muted)' }}>Dépôts :</span>
            {stocksFiltered.map(s => (
              <label key={s.stock_id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs cursor-pointer select-none transition-all"
                style={{
                  borderColor: selectedDepots.includes(s.stock_id) ? 'var(--primary-solid)' : 'var(--border)',
                  background:  selectedDepots.includes(s.stock_id) ? 'color-mix(in srgb, var(--primary-solid) 8%, white)' : 'white',
                  color:       selectedDepots.includes(s.stock_id) ? 'var(--primary-solid)' : 'var(--text-muted)',
                  fontWeight:  selectedDepots.includes(s.stock_id) ? '600' : '400',
                }}>
                <input type="checkbox" className="hidden"
                  checked={selectedDepots.includes(s.stock_id)}
                  onChange={() => toggleDepot(s.stock_id)} />
                {s.nom}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Chips motifs actifs */}
      {motifFilters.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Motifs :</span>
          {motifFilters.map(m => (
            <span key={m}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)', border: '1px solid var(--primary-100)' }}>
              {m}
              <button type="button" onClick={() => toggleMotif(m)}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}

      {/* ── Tableau ──────────────────────────────────────────────────────────── */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>Date</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>Type</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>Article</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>Dépôt / Zone</th>
                <th className="px-3 py-2.5 text-center font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>Avant</th>
                <th className="px-3 py-2.5 text-center font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>Après</th>
                <th className="px-3 py-2.5 text-center font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>Delta</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>Utilisateur</th>
                <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>Motif</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 rounded-full animate-spin"
                        style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
                      Chargement…
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                    Aucun mouvement trouvé
                  </td>
                </tr>
              ) : items.map((item, i) => {
                const meta = TYPE_META[item.type] || { label: item.type, color: '#6B7280', bg: '#F3F4F6' };
                const isDepl = item.type === 'deplacement';
                const depot = item.stock_nom
                  ? (item.zone_nom ? `${item.stock_nom} › ${item.zone_nom}` : item.stock_nom)
                  : '—';
                const depotDest = isDepl && item.stock_dest_nom
                  ? (item.zone_dest_nom ? `${item.stock_dest_nom} › ${item.zone_dest_nom}` : item.stock_dest_nom)
                  : null;

                return (
                  <tr key={item.mouvement_id || i}
                    data-testid="mouvement-global-row"
                    className="border-t transition-colors hover:bg-gray-50/60"
                    style={{ borderColor: 'var(--border)' }}>

                    {/* Date */}
                    <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                      {fmtDate(item.date_mouvement || item.created_at)}
                    </td>

                    {/* Type badge */}
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                        style={{ background: meta.bg, color: meta.color }}>
                        {meta.label}
                      </span>
                    </td>

                    {/* Article */}
                    <td className="px-3 py-2.5">
                      <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {item.article_ref && (
                          <span className="text-xs mr-1.5" style={{ color: 'var(--text-muted)' }}>
                            {item.article_ref}
                          </span>
                        )}
                        {item.article_nom || '—'}
                      </div>
                      {item.marque_nom && (
                        <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                          {item.marque_nom}
                        </div>
                      )}
                    </td>

                    {/* Dépôt / Zone */}
                    <td className="px-3 py-2.5">
                      <div style={{ color: 'var(--text-primary)' }}>{depot}</div>
                      {depotDest && (
                        <div className="flex items-center gap-1 mt-0.5" style={{ color: 'var(--text-muted)' }}>
                          <ArrowLeftRight size={10} />
                          {depotDest}
                        </div>
                      )}
                    </td>

                    {/* Avant */}
                    <td className="px-3 py-2.5 text-center tabular-nums" style={{ color: 'var(--text-muted)' }}>
                      {item.quantite_avant ?? '—'}
                    </td>

                    {/* Après */}
                    <td className="px-3 py-2.5 text-center tabular-nums font-medium" style={{ color: 'var(--text-primary)' }}>
                      {item.quantite_apres ?? '—'}
                    </td>

                    {/* Delta */}
                    <td className="px-3 py-2.5 text-center">
                      <Delta avant={item.quantite_avant} apres={item.quantite_apres} />
                    </td>

                    {/* Utilisateur */}
                    <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                      {item.user_nom || '—'}
                    </td>

                    {/* Motif */}
                    <td className="px-3 py-2.5 max-w-[200px]">
                      <span className="truncate block" style={{ color: 'var(--text-muted)' }}
                        title={item.commentaire || ''}>
                        {item.commentaire || '—'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Pagination ───────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {total} mouvement{total > 1 ? 's' : ''} · page {page + 1} / {totalPages}
          </span>
          <div className="flex gap-2">
            <button type="button" disabled={page === 0} onClick={() => changePage(page - 1)}
              className="p-1.5 rounded-lg border disabled:opacity-40 transition-colors"
              style={{ borderColor: 'var(--border)' }}>
              <ChevronLeft size={14} />
            </button>
            <button type="button" disabled={page >= totalPages - 1} onClick={() => changePage(page + 1)}
              className="p-1.5 rounded-lg border disabled:opacity-40 transition-colors"
              style={{ borderColor: 'var(--border)' }}>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {totalPages <= 1 && total > 0 && (
        <p className="text-xs text-right" style={{ color: 'var(--text-muted)' }}>
          {total} mouvement{total > 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}
